// Recover a tab whose build was replaced underneath it.
//
// A few modules load on demand (StepUpModal, dropFiles, web-vitals), and each
// build gives their chunk files new hashed names. When a deploy lands, the
// new service worker purges the old build from precache and the server no
// longer has those files either -- so a tab opened before the deploy 404s
// the first time it needs one of them. For StepUpModal that means a step-up
// prompt that silently never appears. Any React.lazy route added later would
// fail the same way.
//
// Script and stylesheet load errors don't bubble, but they do reach a
// capture-phase listener on window, so this catches every chunk failure no
// matter which caller awaited the import (or swallowed its rejection).
//
// The fix is a reload onto the current build. That can discard typed input,
// which serviceWorkerRegistration.ts goes out of its way to avoid -- but here
// the action the user just took has already failed and cannot succeed on
// this build, so waiting buys nothing. Guarded so a chunk that is genuinely
// missing from the NEW build can't turn this into a refresh loop.

const GUARD_KEY = "chunk-reload-at";
const GUARD_MS = 30 * 1000;

function isBuildAsset(el: EventTarget | null): boolean {
  const prefix = `${process.env.PUBLIC_URL || ""}/static/`;
  if (el instanceof HTMLScriptElement) {
    return new URL(el.src, window.location.href).pathname.startsWith(prefix);
  }
  if (el instanceof HTMLLinkElement && el.rel === "stylesheet") {
    return new URL(el.href, window.location.href).pathname.startsWith(prefix);
  }
  return false;
}

export function installChunkReload() {
  window.addEventListener(
    "error",
    (event) => {
      if (!isBuildAsset(event.target)) return;
      try {
        const last = Number(sessionStorage.getItem(GUARD_KEY) || 0);
        if (Date.now() - last < GUARD_MS) return;
        sessionStorage.setItem(GUARD_KEY, String(Date.now()));
      } catch {
        // Storage blocked (private mode etc.) -- without a guard we can't
        // rule out a loop, so leave the page as-is rather than risk one.
        return;
      }
      window.location.reload();
    },
    true
  );
}
